import cn from 'classnames';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import RoleContainer from '../../../containers/blocks/RoleContainer';
import UserMenuContainer from '../../../containers/menus/UserMenuContainer';
import { CymbioSpinner } from '../../common/Spinner';
import useScroll from '../../../hooks/useScroll';
import logo from '../../../images/cymbio-logo-only.svg';
import { TAB_REORDER } from '../../../constants/mainConstants';

import styles from './RetailersMenu.module.scss';

function RetailersMenu(props) {
  const { pathname } = useLocation();
  const { retailer } = useSelector((state) => state.role);
  const { auth } = useSelector((state) => state.auth);
  const { isLoading, replenish, administration } = useSelector((state) => state.checkPermissions);
  const { is_super_admin: isSuperAdmin } = useSelector((state) => state.role.user ?? {});

  const hasReplenishAccess = replenish || isSuperAdmin;
  const hasAdminAccess = administration || isSuperAdmin;

  let [sticky, setSticky] = useState(false);

  useScroll(
    ({ prevPos, currPos }) => {
      if (currPos.y > -56) {
        setSticky(false);
      } else if (currPos.y < prevPos.y) {
        setSticky(true);
      } else {
        setSticky(false);
      }
    },
    null,
    { wait: 50 },
  );

  if (!auth && (!retailer || isLoading)) {
    return auth ? <CymbioSpinner shouldCenterVerticallyInViewport /> : null;
  }

  const createMenu = () => {
    const menu = [
      {
        label: 'Catalogue',
        path: `/retailers/${retailer.id}/catalogue-management`,
      },
    ];

    if (hasAdminAccess) {
      menu.push({ label: 'Sellers', path: `/retailers/${retailer.id}/admin/brands` });
    }

    menu.push({ label: 'Orders', path: `/retailers/${retailer.id}/dropship/orders` });

    if (hasReplenishAccess) {
      menu.push({ label: 'Reorder', path: `/retailers/${retailer.id}/reorder` });
    }

    return menu;
  };

  const menuItems = () => {
    const menu = createMenu();

    const items = menu.map((item) => {
      const isActive = pathname.includes(item.path);

      return (
        <li key={item.label}>
          <Link className={isActive ? styles.active : ''} to={item.path}>
            {item.label}
          </Link>
        </li>
      );
    });

    return <ul className={styles.menuItems}>{items}</ul>;
  };

  const menuIcons = () => {
    const { cartVariantsCount } = props;

    const isReplenishPath = pathname.includes(TAB_REORDER);

    return (
      <div className={cn(styles.panel_icons, 'menu_wrapper')}>
        {isReplenishPath && hasReplenishAccess && cartVariantsCount > 0 && (
          <Link to={`/retailers/${retailer.id}/reorder/cart`}>
            <div className={styles.cartCountWrapper}>
              <span className="ic-shopping_cart" title="Cart" />
              <span className={styles.cartCount}>{cartVariantsCount}</span>
            </div>
          </Link>
        )}

        {isReplenishPath && hasReplenishAccess && (
          <Link to={`/retailers/${retailer.id}/reorder/orders`}>
            <span className="ic-list" title="Orders" />
          </Link>
        )}

        {hasAdminAccess && (
          <Link to={`/retailers/${retailer.id}/admin`}>
            <span className="ic-settings" title="Admin" />
          </Link>
        )}

        <RoleContainer />

        <UserMenuContainer />
      </div>
    );
  };

  return (
    <div className={cn(styles.wrapper_static, sticky ? styles.sticky : '')}>
      <header className={styles.headerRetailer}>
        <div className={styles.logo}>
          <Link to="/">
            <img alt="Cymbio Logo" src={logo} />
            <span>{retailer?.name}</span>
          </Link>
        </div>

        <nav>{menuItems()}</nav>

        <div className={styles.panel}>{menuIcons()}</div>
      </header>
    </div>
  );
}

export default RetailersMenu;

RetailersMenu.defaultProps = {
  cartVariantsCount: 0,
};

RetailersMenu.propTypes = {
  cartVariantsCount: PropTypes.number,
};
